import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message, Checkbox, Typography } from "@mimber/ui-web"
import { CheckCircleOutlined } from "@mimber/ui-web"
import { Form, SelectField } from "@mimber/forms"
import { CommunicationTemplateSelect } from "@/components/selects"
import { AudienceSelectorField } from "@/components/fields"
import { useUpdateAnnouncement } from "@mimber/client/mimber-organization"
import {
  UpdateAnnouncementDtoSchema,
  AnnouncementStatus,
  AudienceTargetType,
  CommunicationChannel,
} from "@mimber/schema"
import type { UpdateAnnouncementDto } from "@mimber/schema"
import type { EditAnnouncementFormProps } from "./EditAnnouncementForm.types"
import { AnnouncementPreviewButton } from "@/components/modals"
import { useAutoSave } from "@/core/hooks"

import { PRIORITY_OPTIONS } from "./EditAnnouncementForm.utils"

const { Text } = Typography

export const EditAnnouncementForm: React.FunctionComponent<
  EditAnnouncementFormProps
> = ({ announcement, onSuccess }) => {
  const form = useForm<UpdateAnnouncementDto>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(UpdateAnnouncementDtoSchema) as any,
    mode: "onBlur",
    defaultValues: {
      priority: announcement.priority,
      isPinned: announcement.isPinned,
      targetType:
        announcement.audienceTarget?.targetType ?? AudienceTargetType.ALL,
      emailTemplateId: announcement.emailTemplate?.uuid ?? undefined,
      smsTemplateId: announcement.smsTemplate?.uuid ?? undefined,
      pushTemplateId: announcement.pushTemplate?.uuid ?? undefined,
      tierTargetIds:
        announcement.audienceTarget?.tierTargets?.map(
          (t) => t.membershipTier.uuid
        ) ?? [],
      groupTargetIds:
        announcement.audienceTarget?.groupTargets?.map((g) => g.group.uuid) ??
        [],
      roleTargetIds:
        announcement.audienceTarget?.roleTargets?.map(
          (r) => r.organizationRole.uuid
        ) ?? [],
      memberTargetIds:
        announcement.audienceTarget?.memberTargets?.map(
          (m) => m.membership.uuid
        ) ?? [],
    },
  })

  const {
    control,
    formState: { isValid },
    setValue,
    watch,
  } = form
  const isPinned = watch("isPinned")

  const mutation = useUpdateAnnouncement(announcement.uuid, {
    onSuccess: () => {
      message.success("Announcement updated successfully")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to update announcement: ${error.message}`)
    },
  })

  const autoSaveMutation = useUpdateAnnouncement(announcement.uuid)

  const isDraft = announcement.status === AnnouncementStatus.DRAFT
  const formValues = watch()

  const { lastSavedAt, isAutoSaving } = useAutoSave({
    data: formValues,
    onSave: (data) => autoSaveMutation.mutate(data),
    enabled: isDraft,
    isValid,
    isSaving: mutation.isPending || autoSaveMutation.isPending,
  })

  const onSubmit = (data: UpdateAnnouncementDto) => {
    mutation.mutate(data)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <SelectField
        name="priority"
        label="Priority"
        placeholder="Select priority"
        required
        options={PRIORITY_OPTIONS}
      />

      <AudienceSelectorField
        control={control}
        label="Target Audience"
        disabled={mutation.isPending}
      />

      <CommunicationTemplateSelect
        name="emailTemplateId"
        label="Email Template"
        placeholder="Select email template (optional)"
        channelFilter={CommunicationChannel.EMAIL}
        showQuickCreate
      />

      <CommunicationTemplateSelect
        name="smsTemplateId"
        label="SMS Template"
        placeholder="Select SMS template (optional)"
        channelFilter={CommunicationChannel.SMS}
        showQuickCreate
      />

      <CommunicationTemplateSelect
        name="pushTemplateId"
        label="Push Template"
        placeholder="Select push template (optional)"
        channelFilter={CommunicationChannel.PUSH}
        showQuickCreate
      />

      <div style={{ marginBottom: 16 }}>
        <Checkbox
          checked={isPinned}
          onChange={(e) => setValue("isPinned", e.target.checked)}
          disabled={mutation.isPending}
        >
          Pin this announcement
        </Checkbox>
      </div>

      <Space
        style={{
          justifyContent: "flex-end",
          width: "100%",
          alignItems: "center",
        }}
      >
        {lastSavedAt && !isAutoSaving && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CheckCircleOutlined style={{ marginRight: 4 }} />
            Draft saved {lastSavedAt.toLocaleTimeString()}
          </Text>
        )}
        {isAutoSaving && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Saving...
          </Text>
        )}
        <AnnouncementPreviewButton
          emailTemplateUuid={
            watch("emailTemplateId") ?? announcement.emailTemplate?.uuid
          }
          smsTemplateUuid={
            watch("smsTemplateId") ?? announcement.smsTemplate?.uuid
          }
          pushTemplateUuid={
            watch("pushTemplateId") ?? announcement.pushTemplate?.uuid
          }
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}
        >
          Save Changes
        </Button>
      </Space>
    </Form>
  )
}
